/**
 * @file features/page.tsx
 * @description BIDIX 서비스 기능 소개 페이지 (Feature Tour)
 *
 * 주요 기능:
 * 1. 서비스의 핵심 기능 소개 (데이터 수집, 권리 체크, 수익 시뮬레이션, 시뮬레이션 학습)
 * 2. 각 기능별 UI Mockup 시각화
 * 3. 중립적 톤앤매너 유지 (판단/분석/추천 배제, 정리/확인/계산 강조)
 *
 * 핵심 구현 로직:
 * - Client Component로 구현
 * - Tailwind CSS와 div만으로 UI Mockup 구현 (이미지 미사용)
 * - Zinc 계열 색상 메인, Indigo 포인트 컬러 최소화
 * - 반응형 디자인 (모바일 우선)
 * - 다크 모드 지원
 *
 * @dependencies
 * - @/components/shared/ui/button: Button 컴포넌트
 * - lucide-react: 아이콘
 */

"use client";

import Link from "next/link";
import { useAuth } from "@clerk/nextjs";
import { trackEvent } from "@/lib/utils/analytics";
import {
  Info,
  ArrowRight,
  PlayCircle,
} from "lucide-react";
import { Button } from "@/components/shared/ui/button";

export default function FeaturesPage() {
  const { isLoaded, userId } = useAuth();

  return (
    <div className="features-page min-h-screen bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100">
      {/* Hero Section */}
      <section className="py-16 sm:py-24 md:py-32 text-center bg-zinc-50 dark:bg-zinc-900 border-b border-zinc-100 dark:border-zinc-800 w-full px-4 sm:px-6">
        <div className="max-w-3xl mx-auto space-y-4 sm:space-y-6">
          <span className="text-zinc-500 dark:text-zinc-400 font-bold tracking-widest text-[10px] sm:text-xs uppercase">
            Auction Review Workbench
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight px-2 sm:px-0">
            사건 검토와 판단 과정을,
            <br className="hidden sm:block" />
            <span className="text-zinc-400 dark:text-zinc-500">비딕스</span>가
            정리해 드립니다.
          </h1>
          <p className="text-base sm:text-lg text-zinc-600 dark:text-zinc-400 leading-relaxed px-2 sm:px-0">
            경매 물건 데이터를 한곳에 모으고, 검토 흐름을 구조화했습니다.
            <br className="hidden sm:block" />
            반복적인 확인 작업은 비딕스에 맡기고,{" "}
            <strong>검토 시간을 단축</strong>하세요.
          </p>
        </div>
      </section>

      {/* Flow 1: 데이터 수집 (좌측 텍스트 / 우측 Mockup) */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 border-b border-zinc-100 dark:border-zinc-800">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8 sm:gap-12 md:gap-16 items-center">
          {/* Text */}
          <div className="space-y-4 sm:space-y-6 order-2 md:order-1">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-lg flex items-center justify-center font-bold text-base sm:text-lg">
              1
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight">
              흩어진 부동산 경매 사건 공시 정보를
              <br className="hidden sm:block" />
              한눈에 확인하세요.
            </h2>
            <p className="text-sm sm:text-base text-zinc-500 dark:text-zinc-400 leading-relaxed">
              법원경매정보 사이트의 정보를 일일이 확인하는 번거로움을 줄였습니다.
              사건번호만 입력하면 감정가, 최저가, 기일 내역 등{" "}
              <strong>공시된 사실 데이터</strong>를 정리해서 보여드립니다.
            </p>
          </div>
          {/* Visual (Data Card Mockup) */}
          <div className="order-1 md:order-2 bg-zinc-100 dark:bg-zinc-800 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 flex items-center justify-center">
            <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-lg p-4 sm:p-6 w-full max-w-sm border border-zinc-200 dark:border-zinc-700">
              {/* Search Bar */}
              <div className="flex justify-between items-center mb-4">
                <div className="h-4 w-1/3 bg-zinc-800 dark:bg-zinc-200 rounded"></div>
                <div className="h-4 w-1/4 bg-zinc-200 dark:bg-zinc-700 rounded"></div>
              </div>
              {/* Data Lines */}
              <div className="space-y-2 mb-6">
                <div className="h-3 w-full bg-zinc-100 dark:bg-zinc-800 rounded"></div>
                <div className="h-3 w-2/3 bg-zinc-100 dark:bg-zinc-800 rounded"></div>
                <div className="h-3 w-4/5 bg-zinc-100 dark:bg-zinc-800 rounded"></div>
              </div>
              {/* Tags */}
              <div className="pt-4 border-t border-zinc-100 dark:border-zinc-800 flex gap-2">
                <span className="px-2 py-1 bg-zinc-100 dark:bg-zinc-800 rounded text-xs text-zinc-500 dark:text-zinc-400">
                  감정가 정보
                </span>
                <span className="px-2 py-1 bg-zinc-100 dark:bg-zinc-800 rounded text-xs text-zinc-500 dark:text-zinc-400">
                  최저가 정보
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Flow 2: 권리 체크 (우측 텍스트 / 좌측 Mockup - ZigZag) */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 border-b border-zinc-100 dark:border-zinc-800">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8 sm:gap-12 md:gap-16 items-center">
          {/* Visual (Checklist Mockup) */}
          <div className="bg-zinc-900 dark:bg-zinc-950 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 flex items-center justify-center relative overflow-hidden">
            <div className="bg-zinc-800 dark:bg-zinc-900 rounded-xl shadow-2xl p-4 sm:p-6 w-full max-w-sm border border-zinc-700 dark:border-zinc-800 text-white z-10">
              <div className="mb-3 sm:mb-4 text-xs sm:text-sm text-zinc-400 dark:text-zinc-500 font-bold">
                주요 확인 항목
              </div>
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-300 dark:text-zinc-400 text-xs sm:text-sm">
                    말소기준권리 날짜
                  </span>
                  <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-zinc-500 dark:border-zinc-600 rounded flex-shrink-0"></div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-zinc-300 dark:text-zinc-400 text-xs sm:text-sm">
                    임차인 전입일 비교
                  </span>
                  <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 bg-indigo-500 border-2 border-indigo-500 rounded flex items-center justify-center flex-shrink-0">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-sm"></div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-zinc-300 dark:text-zinc-400 text-xs sm:text-sm">
                    비고란 기재 사항
                  </span>
                  <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-zinc-500 dark:border-zinc-600 rounded flex-shrink-0"></div>
                </div>
              </div>
            </div>
          </div>
          {/* Text */}
          <div className="space-y-4 sm:space-y-6">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-lg flex items-center justify-center font-bold text-base sm:text-lg">
              2
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight">
              주요 확인 필요 항목,
              <br className="hidden sm:block" />
              체크리스트로 정리하세요.
            </h2>
            <p className="text-sm sm:text-base text-zinc-500 dark:text-zinc-400 leading-relaxed">
              권리관계는 시스템이 판단하지 않습니다. 대신,{" "}
              <strong>사용자가 직접 검토해야 할 주요 항목</strong>을 리스트로
              제공하여, 빠짐없이 확인할 수 있도록 돕습니다.
            </p>
          </div>
        </div>
      </section>

      {/* Flow 3: 손익 계산 (좌측 텍스트 / 우측 Mockup) */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 border-b border-zinc-100 dark:border-zinc-800">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8 sm:gap-12 md:gap-16 items-center">
          {/* Text */}
          <div className="space-y-4 sm:space-y-6 order-2 md:order-1">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-lg flex items-center justify-center font-bold text-base sm:text-lg">
              3
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight">
              입력한 조건 기준으로
              <br className="hidden sm:block" />
              예상 비용을 계산합니다.
            </h2>
            <p className="text-sm sm:text-base text-zinc-500 dark:text-zinc-400 leading-relaxed">
              입찰가와 매도가를 가정해 보세요.
              <br className="hidden sm:block" />
              선택하신 세금 기준과 비용을 반영하여,{" "}
              <strong>입력값에 따른 단순 계산 결과</strong>를 즉시 산출해
              드립니다.
            </p>
          </div>
          {/* Visual (Calculator Mockup) */}
          <div className="order-1 md:order-2 bg-indigo-50 dark:bg-indigo-950/20 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 flex items-center justify-center">
            <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-xl border border-indigo-100 dark:border-indigo-900/50 p-4 sm:p-6 w-full max-w-sm">
              <div className="text-[10px] sm:text-xs font-bold text-zinc-400 dark:text-zinc-500 mb-3 sm:mb-4 uppercase">
                Calculation Result
              </div>
              <div className="flex justify-between items-end mb-2">
                <span className="text-xs sm:text-sm text-zinc-600 dark:text-zinc-400">
              손익 계산 결과
                </span>
                <span className="text-xl sm:text-2xl font-extrabold text-indigo-600 dark:text-indigo-400">
                  + 5,000만
                </span>
              </div>
              <div className="h-1.5 w-full bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden mb-3 sm:mb-4">
                <div className="h-full w-2/3 bg-indigo-500 dark:bg-indigo-600"></div>
              </div>
              <div className="text-[9px] sm:text-[10px] text-zinc-400 dark:text-zinc-500 text-right">
                * 사용자 입력 가정값 기준
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Flow 4: 시뮬레이션 체험 (우측 텍스트 / 좌측 Mockup - ZigZag) */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8 sm:gap-12 md:gap-16 items-center">
          {/* Visual (Simulation Learning Mockup) */}
          <div className="bg-zinc-100 dark:bg-zinc-800 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 flex items-center justify-center">
            <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-lg p-4 sm:p-6 w-full max-w-sm border border-zinc-200 dark:border-zinc-700">
              {/* Header */}
              <div className="mb-3 sm:mb-4 flex items-center gap-2">
                <PlayCircle className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-600 dark:text-zinc-400" />
                <div className="text-xs sm:text-sm font-bold text-zinc-900 dark:text-zinc-100">
                  시뮬레이션 생성
                </div>
              </div>
              {/* Difficulty Selection */}
              <div className="mb-3 sm:mb-4">
                <div className="text-[10px] sm:text-xs text-zinc-500 dark:text-zinc-400 mb-2 font-medium">
                  난이도 선택
                </div>
                <div className="flex gap-1.5 sm:gap-2">
                  <div className="flex-1 px-2 sm:px-3 py-1.5 sm:py-2 bg-zinc-100 dark:bg-zinc-800 rounded-lg text-center text-[10px] sm:text-xs font-medium text-zinc-700 dark:text-zinc-300">
                    Easy
                  </div>
                  <div className="flex-1 px-2 sm:px-3 py-1.5 sm:py-2 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-lg text-center text-[10px] sm:text-xs font-bold">
                    Normal
                  </div>
                  <div className="flex-1 px-2 sm:px-3 py-1.5 sm:py-2 bg-zinc-100 dark:bg-zinc-800 rounded-lg text-center text-[10px] sm:text-xs font-medium text-zinc-700 dark:text-zinc-300">
                    Hard
                  </div>
                </div>
              </div>
              {/* Learning Purpose Selection */}
              <div className="mb-3 sm:mb-4">
                <div className="text-[10px] sm:text-xs text-zinc-500 dark:text-zinc-400 mb-2 font-medium">
                  학습 목적
                </div>
                <div className="space-y-1.5 sm:space-y-2">
                  <div className="px-2 sm:px-3 py-1.5 sm:py-2 bg-zinc-50 dark:bg-zinc-800 rounded-lg text-[10px] sm:text-xs text-zinc-600 dark:text-zinc-400">
                    첫 입찰 연습
                  </div>
                  <div className="px-2 sm:px-3 py-1.5 sm:py-2 bg-zinc-50 dark:bg-zinc-800 rounded-lg text-[10px] sm:text-xs text-zinc-600 dark:text-zinc-400">
                    권리 분석 연습
                  </div>
                  <div className="px-2 sm:px-3 py-1.5 sm:py-2 bg-zinc-50 dark:bg-zinc-800 rounded-lg text-[10px] sm:text-xs text-zinc-600 dark:text-zinc-400">
                    수익성 분석 연습
                  </div>
                </div>
              </div>
              {/* Badge */}
              <div className="pt-3 sm:pt-4 border-t border-zinc-100 dark:border-zinc-800">
                <span className="px-2 py-1 bg-zinc-100 dark:bg-zinc-800 rounded text-[10px] sm:text-xs text-zinc-500 dark:text-zinc-400">
                  가상 매물로 안전하게 연습
                </span>
              </div>
            </div>
          </div>
          {/* Text */}
          <div className="space-y-4 sm:space-y-6">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-lg flex items-center justify-center font-bold text-base sm:text-lg">
              4
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight">
              보조 학습 기능도
              <br className="hidden sm:block" />
              함께 활용할 수 있습니다.
            </h2>
            <p className="text-sm sm:text-base text-zinc-500 dark:text-zinc-400 leading-relaxed">
              실제 사건번호가 아닌 <strong>가상 매물</strong>로 검토 흐름을
              익힐 수 있습니다. 난이도와 학습 목적을 선택하면 BIDIX가 일관성 있는
              시나리오를 생성합니다. 메인 기능은 아니지만, 입문자가 구조를
              익히는 데에는 유용합니다.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-24 bg-zinc-900 dark:bg-zinc-950 text-white text-center w-full px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 md:mb-8 leading-tight">
            사건 정보 확인과 계산,
            <br className="hidden sm:block" />한 번에 정리하세요.
          </h2>
          <p className="text-zinc-400 dark:text-zinc-500 mb-6 sm:mb-8 md:mb-10 text-base sm:text-lg max-w-2xl mx-auto px-2 sm:px-0">
            지금 바로 사건번호를 입력하고 공시 데이터와 검토 흐름을 확인해
            보세요.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-white text-zinc-900 hover:bg-zinc-100 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-zinc-200 px-6 sm:px-8 py-5 sm:py-6 rounded-xl font-bold text-base sm:text-lg shadow-lg transform hover:-translate-y-1 transition-all min-h-[48px] touch-manipulation"
          >
            <Link
              href="/list"
              className="flex items-center justify-center gap-2"
              onClick={() => {
                trackEvent(
                  "landing_page_click",
                  {
                    source: "features_page",
                    button_location: "cta_section",
                    redirect_to: "list",
                    user_type:
                      isLoaded && userId ? "authenticated" : "anonymous",
                  },
                  "navigation",
                  { includeTrafficSource: true },
                );
              }}
            >
              사건번호 검색하기
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Disclaimer Section */}
      <section className="bg-zinc-50 dark:bg-zinc-900 border-t border-zinc-200 dark:border-zinc-800 py-6 sm:py-8 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto flex gap-3 sm:gap-4 items-start text-zinc-500 dark:text-zinc-400">
          <Info className="w-4 h-4 sm:w-5 sm:h-5 mt-0.5 sm:mt-1 flex-shrink-0" />
          <p className="text-xs sm:text-sm leading-relaxed">
            <strong>서비스 이용 안내:</strong>
            <br />본 서비스는 법원 경매 정보 사이트의 공시 데이터를 기반으로
            정보를 정리하여 제공하며, 사용자가 입력한 가정값에 따른 단순 계산
            결과(시뮬레이션)를 보여주는 도구입니다. 비딕스는 특정 물건에 대한
            투자를 권유하거나, 권리관계 및 수익성을 보장하지 않습니다. 실제
            권리관계, 세금, 비용은 공부 서류 발급 및 현장 조사를 통해 반드시
            본인이 직접 확인해야 하며, 
            실제 투자 결정에 앞서 전문가와 상담하시기 바랍니다.
          </p>
        </div>
      </section>
    </div>
  );
}
